import { hasGroupBounds } from "@/components/expression/utils/matrix";
import { insertNode } from "@/input/utils/editOperators";
import { cursorIsActive } from "@/models/CursorModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import type { NodeModel } from "@/models/nodes/types";
import type { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import type { ClosingSymbol, GroupNodeProps, OpeningSymbol } from "@corca/data";

export function canGroupSelection(doc: DocumentModel): boolean {
  const {
    selection: { isContinuousSelection },
  } = doc;
  return cursorIsActive(doc.cursor) && isContinuousSelection;
}

export function groupSelection(
  doc: DocumentModel,
  openingSymbol: OpeningSymbol,
  closingSymbol: ClosingSymbol,
  groupProps?: Omit<GroupNodeProps, "openingSymbol" | "closingSymbol">,
): GroupNodeModel | undefined {
  if (cursorIsActive(doc.cursor) && canGroupSelection(doc)) {
    const { selectedNodes } = doc.selection;
    const group = doc.create(GroupNodeModel, [], openingSymbol, closingSymbol, groupProps);

    insertNode(doc.cursor, group);

    if (group.inlineNode) {
      group.inlineNode.appendChildren(selectedNodes);
      doc.cursor.moveTo(group.inlineNode, group.inlineNode.nodes.length);

      return group;
    }
  }

  return undefined;
}

/**
 * Find groups that you can ungroup:
 * - If there is a selection of nodes, it will find parent groups of all selected nodes.
 * - If there is no selection, it will find the first parent group of the current cursor.
 * - If the cursor is on group already, return this group.
 *
 * Will return empty array if no groups found.
 */
function groupsToUngroup(doc: DocumentModel): GroupNodeModel[] {
  const { cursor, selection } = doc;

  let nodes: NodeModel[] = [];

  if (selection.isContinuousSelection) {
    nodes = selection.selectedNodes;
  } else if (cursorIsActive(cursor)) {
    nodes = [cursor.node];
  }

  if (nodes.length === 0) return [];

  function isUngroupable(node?: NodeModel): node is Maybe<GroupNodeModel> {
    if (node?.is(GroupNodeModel) && node.matrixNode) {
      return !node.casesSystemEnabled && hasGroupBounds(node.matrixNode?.bounds);
    }

    return false;
  }

  const groups = nodes.filter(isUngroupable);

  if (groups.length > 0) return groups;

  const ancestors = nodes[0].ancestors.filter((n) => !n.is(InlineNodeModel));

  if (isUngroupable(ancestors[0])) {
    return [ancestors[0]];
  }

  if (ancestors[0]?.is(MatrixNodeModel) && isUngroupable(ancestors[1])) {
    return [ancestors[1]];
  }

  return [];
}

export function ungroup(doc: DocumentModel): void {
  groupsToUngroup(doc).forEach((group) => {
    group.inlineNode?.nodes.forEach((n) => group.insertSiblingBefore(n));
    doc.cursor.moveBeforeNode(group);
    doc.removeNode(group);
    doc.selection.resetSelection();
  });
}

export function convertParametersToGroup(node: FunctionNodeModel | VariableNodeModel) {
  const { doc } = node;
  const flattened = node.parameters.reduce<NodeModel[]>((acc, n) => [...acc, ...n.nodes], []);
  const groupNode = doc.create(
    GroupNodeModel,
    flattened,
    node.openingSymbol ?? "(",
    node.closingSymbol ?? ")",
  );

  return groupNode;
}
